package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.site.api.Async
import dev.lingvo.site.i18n.t
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.CSSSizeValue
import org.jetbrains.compose.web.css.CSSUnit
import org.jetbrains.compose.web.dom.Div

/**
 * The three states a loaded thing can be in, and the settle that carries it into the third.
 *
 * **The triad was written out by hand in twenty-six files**, and the one file that did not write all
 * of it shipped the bug this component exists to make impossible: `General.kt` had a `Success` arm
 * and an `else`, so an owner whose request failed sat on "Loading what this would remove…" for good
 * — no error, no retry, and no delete button. A `when` over a sealed class does not warn you about a
 * branch you folded into `else`, and three files had quietly folded one.
 *
 * Passing the content as a slot means [Async.Failure] cannot be forgotten, because there is nowhere
 * left to forget it.
 *
 * **The entrance, which is the other reason this is one component.** A skeleton being replaced by
 * real content is the moment nearly every screen in the product visibly changes, and it snapped.
 * Animating it per page would have meant every page deciding for itself how content arrives, which
 * is how a product ends up with four different entrances; here the decision is made once.
 * [enterRise] is a 6px rise over [Motion.StandardMs] and nothing else — no fade, so a tab the
 * browser has stopped painting cannot strand the content invisible, and no delay, because this is
 * one panel arriving rather than a list assembling. Under `prefers-reduced-motion` it does not run
 * at all and the content is simply there.
 *
 * **Once per panel, not once per fetch.** `loadAsync` sets [Async.Loading] on every key change, so a
 * filter or a range control that refetches unmounts this whole subtree and mounts it again — and
 * the entrance replayed each time, along with every `enterStaggered` card inside it. Insights is the
 * clearest case: pressing 7 / 30 / 90 re-ran the panel's rise and its four staggered tiles, so
 * narrowing a date range looked exactly like loading the page. An entrance says *this arrived*; a
 * refetch of a panel the reader is already looking at is not an arrival, and saying it again is how
 * motion stops meaning anything. The decision is remembered inside the success branch rather than
 * recomputed, the way [LRouteTransition] remembers `entering` inside its `key`: a later
 * recomposition changing its mind would strip the animation off the element mid-flight.
 *
 * **The wrapper element is the part that needed care.** The animation needs an element to run on,
 * and introducing one between a page's `Column` and its content broke that content twice over:
 *
 * - *Width.* [LoadingState] forces `width: 100%` on itself, but a bare `<div>` inside one of this
 *   product's `flex-start` columns is a flex item that shrinks to its own content. A member table
 *   that had always filled its card collapsed to `max-content` the moment it finished loading, so
 *   the entrance this component exists to smooth arrived as a width jump. Hence `fillMaxWidth` and
 *   `align-items: stretch` — the latter because Kobweb's own `Column` defaults to `flex-start`, and
 *   under that an `LCard(Modifier.fillMaxWidth())` resolves its 100% against fit-content.
 * - *Spacing.* A success arm that emitted several siblings used to get the page column's `gap`
 *   between them. Wrapped, they are one child, and the gap falls to the wrapper's outside — the
 *   billing page's alert, numbers and invoice table stacked flush against each other. [gap] puts
 *   it back, on the wrapper, where those siblings now live.
 *
 * The caller's [modifier] is applied last, so a surface that wants a narrower measure can still
 * say so.
 */
@Composable
fun <T> LAsyncContent(
    state: Async<T>,
    modifier: Modifier = Modifier,
    rows: Int = 3,
    loadingLabel: String = t("ds.ds.lasync_content.loading_content", "Loading content"),
    subject: String = t("ds.ds.lasync_content.this_page", "this page"),
    faultMessage: String? = null,
    /**
     * The spacing between the success slot's children — the page column's own `gap`, usually.
     *
     * Required whenever the slot emits more than one element, because those elements are no longer
     * children of the page's column and stopped inheriting its spacing. A slot with a single child
     * needs nothing.
     */
    gap: CSSSizeValue<CSSUnit.px>? = null,
    onRetry: (() -> Unit)? = null,
    /**
     * A page's own words for a failure, where the default sentence is not good enough.
     *
     * Nineteen surfaces already write their own, and they are right to: on a money page
     * "We can't read the ledger right now. Nothing has changed and nothing has been charged" says
     * something the generic refusal cannot, and echoing the server's message there is at best
     * jargon and at worst implies a charge that never happened. The slot exists so having better
     * copy is not a reason to hand-roll the whole triad again.
     */
    failure: (@Composable (Async.Failure) -> Unit)? = null,
    content: @Composable (T) -> Unit,
) {
    var arrivedOnce by remember { mutableStateOf(false) }
    when (state) {
        is Async.Loading -> LoadingState(modifier, rows = rows, label = loadingLabel)
        // **Deliberately unwrapped, and it looks like an omission beside the success arm below.**
        // The failure arm already enters: every branch of [AsyncFailureState] renders either an
        // `LEmptyState` or an `LAlert`, and both animate themselves. Wrapping this to "match" the
        // success arm would give a refusal two entrances rather than one.
        //
        // The success arm needs its wrapper because most slots are ordinary content that would
        // otherwise arrive with nothing — and it can have it without compounding, because
        // [LocalEntranceOwned] tells anything inside that already animates to stand down.
        // `AsyncContentEntranceTest` pins exactly one animated element per arm.
        is Async.Failure -> failure?.invoke(state) ?: AsyncFailureState(
            state,
            modifier,
            subject = subject,
            faultMessage = faultMessage,
            onRetry = onRetry,
        )
        is Async.Success -> {
            // Remembered inside the branch, so it is decided once per arrival: the branch is
            // discarded when this panel goes back to loading and recomputed when it returns.
            val firstArrival = remember { !arrivedOnce }
            // **True for the length of an arrival — every arrival, not only the first.**
            //
            // A refetch does not animate the wrapper, but it does remount the slot wholesale, so
            // whatever is inside must be held for the same window or it announces itself as newly
            // arrived on a panel the reader never looked away from. Starting this at `firstArrival`
            // meant a refetch had to be covered by widening the ownership expression instead —
            // which latched it to `true` for the rest of the subtree's life, so on any panel that
            // had reloaded once, an `LAlert` appearing later arrived flat for ever. That is the
            // notice after an action, and it is exactly what `LAlert`'s entrance is for.
            //
            // Ownership is a property of an arrival and it expires. Starting true on both paths is
            // what lets it.
            var entranceRunning by remember { mutableStateOf(true) }
            if (entranceRunning) {
                LaunchedEffect(Unit) {
                    delay(Motion.StandardMs.toLong())
                    entranceRunning = false
                }
            }
            Div(
                Modifier
                    .fillMaxWidth()
                    .styleModifier {
                        property("display", "flex")
                        property("flex-direction", "column")
                        property("align-items", "stretch")
                        gap?.let { property("gap", it.toString()) }
                    }
                    .then(modifier)
                    .then(if (firstArrival) Modifier.enterRise() else Modifier)
                    .toAttrs(),
            ) {
                // Anything in this slot that animates itself defers *while this wrapper is
                // animating*, and not a moment longer.
                //
                // Providing a flat `true` was wrong, and wrong in a way that is easy to miss: it
                // held for the life of the subtree, so an `LAlert` appearing later — the "that
                // didn't work" notice after an action, which is exactly what `LAlert`'s entrance is
                // for — would have found the flag still set and arrived flat. Ownership is a
                // property of the arrival, not of the container.
                // Owned exactly while this arrival is settling, on either path, and never after.
                CompositionLocalProvider(LocalEntranceOwned provides entranceRunning) {
                    content(state.value)
                }
            }
            SideEffect { arrivedOnce = true }
        }
    }
}
